import React from "react";
import { ArrowRight, ShieldCheck, Stethoscope, Syringe } from "lucide-react";
import { trackEvent } from "../../lib/analytics";

export const Hero = ({ onBookClick, onSecondaryClick }) => {
  return (
    <section id="top" className="relative pt-32 md:pt-36 pb-16 md:pb-24 overflow-hidden">
      {/* Soft mint blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-[#E8F0E9] blur-3xl opacity-70 animate-blob"></div>
      <div aria-hidden className="pointer-events-none absolute top-32 -right-24 h-[420px] w-[420px] rounded-full bg-[#FCE9E2] blur-3xl opacity-80 animate-blob"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <span
            data-testid="hero-pill"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E8F0E9] px-3.5 py-1.5 text-xs font-medium text-[#779D7C]"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#EB8771]" /> Hyderabad's trusted Mounjaro doctor
          </span>
          <h1 className="mt-6 font-display text-[44px] sm:text-6xl md:text-7xl leading-[0.98] text-[#242220]">
            <span className="sr-only">Mounjaro Doctor in Hyderabad — </span>
            Lose <span className="italic text-[#779D7C]">10–30 kgs</span>,
            <br className="hidden md:block" /> safely & sustainably.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#38312C] leading-relaxed max-w-2xl">
            A doctor‑led medical weight loss programme using <b className="text-[#242220]">Mounjaro (Tirzepatide)</b> and
            other GLP‑1 therapies — personally supervised by{" "}
            <b className="text-[#242220]">Dr. Shalini Patlolla</b>, senior endocrinologist (MD, DM SVIMS) and founder of
            Asian Diabetes Thyroid Hormone Superspeciality Centre, Madinaguda, Hyderabad.
            In‑clinic + online consultations across <b className="text-[#242220]">Telangana & Andhra Pradesh</b>.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="hero-book-btn"
              onClick={() => { trackEvent("quiz_started", { location: "hero" }); onBookClick(); }}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#EB8771] hover:bg-[#E2725C] text-white px-7 py-4 text-[15px] font-medium shadow-[0_14px_30px_-14px_rgba(255,122,89,0.65)] transition-all hover:scale-[1.02]"
            >
              Check My Eligibility · 30s Quiz <ArrowRight className="h-4 w-4" />
            </button>
            <a
              data-testid="hero-whatsapp-btn"
              href="https://wa.me/919133552279?text=Hi%20Dr.%20Shalini%2C%20I%27m%20interested%20in%20Mounjaro%20weight%20loss%20consultation."
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("whatsapp_clicked", { location: "hero" })}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-[#779D7C]/15 text-[#779D7C] hover:bg-[#E8F0E9] px-7 py-4 text-[15px] font-medium transition-colors"
            >
              WhatsApp 91335 52279
            </a>
          </div>

          {/* Micro trust stats — real numbers from asiandiabeticcenter.in */}
          <div className="mt-12 grid grid-cols-3 gap-3 max-w-xl">
            {[
              { icon: Stethoscope, k: "10,000+", v: "patients treated" },
              { icon: Syringe, k: "98%", v: "satisfaction rate" },
              { icon: ShieldCheck, k: "2", v: "clinics in Hyderabad" },
            ].map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`} className="rounded-2xl bg-white border border-[#EAE7E1] p-4">
                <s.icon className="h-4 w-4 text-[#EB8771]" strokeWidth={1.8} />
                <p className="mt-3 font-display text-2xl text-[#242220]">{s.k}</p>
                <p className="text-xs text-[#6B6660] mt-0.5">{s.v}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative animate-fade-up">
          <div className="relative aspect-[4/5] rounded-[36px] overflow-hidden bg-[#E8F0E9] border border-white shadow-[0_30px_60px_-30px_rgba(15,95,74,0.35)]">
            <img
              src="https://www.asiandiabeticcenter.in/wp-content/uploads/2026/02/dr.shalini-best-diabetes-doctor-m7Vb0brKL9hygeba-1.jpg"
              alt="Dr. Shalini Patlolla — Senior Endocrinologist, Asian Diabetes Thyroid Hormone Superspeciality Centre"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#242220]/70 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <p className="text-[11px] tracking-[0.22em] uppercase opacity-80">Featured Therapy</p>
              <p className="font-display text-3xl mt-1">Mounjaro · Tirzepatide</p>
            </div>
          </div>

          {/* Floating card */}
          <div className="hidden md:block absolute -left-6 -bottom-10 w-72 bg-white rounded-3xl border border-[#EAE7E1] p-5 shadow-[0_12px_40px_-16px_rgba(10,41,32,0.18)]">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B6660]">Programme Outcome</p>
            <p className="font-display text-[28px] mt-1 text-[#242220] leading-tight">
              Lose 10–30 kgs<br />in 6 months
            </p>
            <p className="text-sm text-[#6B6660] mt-2">supervised personally by Dr. Shalini</p>
          </div>
        </div>
      </div>
    </section>
  );
};
